import { AuthorityDto, UserDto } from "apis/data-contracts";
import { Table } from "components/Common/Table/Table";
import Button from "components/foundation/Button";
import ButtonDropdown from "components/foundation/ButtonDropdown/ButtonDropdown";
import Modal from "components/foundation/Modal/Modal";
import Toast from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import dayjs from "dayjs";
import useUserApprovalMutation from "features/user/hooks/mutations/useUserApprovalMutation";
import useUserAuthoritiesQuery from "features/user/hooks/queries/useUserAuthoritiesQuery";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { match } from "ts-pattern";

interface UserTableRowProps {
  id: string;
  no: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  department: string;
  position: string;
  joinDate: string;
  userStatus: UserDto["approveStatus"];
}

const UserTableRow = ({
  id,
  no,
  name,
  email,
  phone,
  company,
  department,
  position,
  joinDate,
  userStatus,
}: UserTableRowProps) => {
  const theme = useTheme();
  const { mutate } = useUserApprovalMutation();
  const navigate = useNavigate();
  const { data: authoritiesData } = useUserAuthoritiesQuery();
  const [selectedAuthority, setSelectedAuthority] =
    useState<AuthorityDto | null>(null);

  const [isAccessModal, setAccessModal] = useState(false);
  const [isSuccessToast, setSuccessToast] = useState(false);
  const [isFailToast, setFailToast] = useState(false);

  const handleDeny = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    mutate(
      { userUuid: id, approval: false },
      {
        onSuccess: () => {
          setFailToast(true);
        },
      },
    );
  };

  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (selectedAuthority) {
      mutate(
        {
          userUuid: id,
          approval: true,
          authorityUuid: selectedAuthority.uuid,
        },
        {
          onSuccess: () => {
            setAccessModal(false);
            setSuccessToast(true);
          },
        },
      );
    }
  };

  return (
    <>
      <Table.Tr
        style={{
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (userStatus !== "WAIT")
            navigate(ROUTER_PATH.user.dashboard.detail(id));
        }}
      >
        <Table.Td>{no.toString()}</Table.Td>
        <Table.Td>{email}</Table.Td>
        <Table.Td>{name}</Table.Td>
        <Table.Td>{phone}</Table.Td>
        <Table.Td>{company}</Table.Td>
        <Table.Td>{department}</Table.Td>
        <Table.Td>{position}</Table.Td>
        <Table.Td>{dayjs(joinDate).format("YYYY-MM-DD")}</Table.Td>
        <Table.Td>
          <Toast.Message open={isSuccessToast} onOpenChange={setSuccessToast}>
            해당 유저의 가입이 승인되었습니다.
          </Toast.Message>
          <Toast.Message open={isFailToast} onOpenChange={setFailToast}>
            해당 유저의 가입이 거절되었습니다.
          </Toast.Message>
          {match(userStatus)
            .with("APPROVE", () => (
              <span style={{ color: theme.color.brick[600] }}>승인</span>
            ))
            .with("DENY", () => (
              <span style={{ color: theme.color.text.danger }}>거절</span>
            ))
            .with("WAIT", () => (
              <ButtonWrapper>
                <Modal.Root open={isAccessModal} onOpenChange={setAccessModal}>
                  <ButtonDropdown
                    list={authoritiesData.map((authority) => ({
                      label: authority?.name,
                      width: "fit-content",
                      onClick: (e) => {
                        e.stopPropagation();
                        setAccessModal(true);
                        setSelectedAuthority(authority);
                      },
                    }))}
                  >
                    <Button size="small">승인</Button>
                  </ButtonDropdown>
                  <Modal.Body
                    title="가입을 승인 하시겠습니까??"
                    onConfirm={handleConfirm}
                    onCancel={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Modal.Root>

                <Modal.Root>
                  <Modal.Trigger
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    asChild
                  >
                    <Button variant="tertiary" size="small">
                      거절
                    </Button>
                  </Modal.Trigger>
                  <Modal.Body
                    title="가입을 거절하시겠습니까?"
                    onConfirm={handleDeny}
                    onCancel={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Modal.Root>
              </ButtonWrapper>
            ))
            .otherwise(() => (
              <></>
            ))}
        </Table.Td>
      </Table.Tr>
    </>
  );
};

export default UserTableRow;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`;
